'use client';

import { useCallback, useSyncExternalStore } from 'react';

export type ConsentStatus = 'pending' | 'accepted' | 'declined';

const COOKIE_NAME = 'cookie_consent';

function readConsentCookie(): ConsentStatus {
  if (typeof document === 'undefined') return 'pending';
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`)
  );
  if (!match) return 'pending';
  const val = match[1];
  if (val === 'accepted' || val === 'declined') return val;
  return 'pending';
}

function writeConsentCookie(value: 'accepted' | 'declined') {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `${COOKIE_NAME}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

let listeners: Array<() => void> = [];
let snapshot: ConsentStatus = 'pending';

function getSnapshot() {
  return snapshot;
}

function getServerSnapshot(): ConsentStatus {
  return 'pending';
}

function subscribe(listener: () => void) {
  listeners = [...listeners, listener];
  // Read current value on subscribe (client-side)
  snapshot = readConsentCookie();
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function emitChange(value: ConsentStatus) {
  snapshot = value;
  for (const listener of listeners) {
    listener();
  }
}

export function useCookieConsent() {
  const status = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot
  );

  const accept = useCallback(() => {
    writeConsentCookie('accepted');
    emitChange('accepted');
  }, []);

  const decline = useCallback(() => {
    writeConsentCookie('declined');
    emitChange('declined');
  }, []);

  return { status, accept, decline };
}
